import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalLoadingService {
  private readonly _isLoading = signal(false);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  get isLoading() {
    return this._isLoading.asReadonly();
  }

  private readonly START_EVENTS = [NavigationStart, RouteConfigLoadStart];
  private readonly END_EVENTS = [
    NavigationEnd,
    RouteConfigLoadEnd,
    NavigationCancel,
    NavigationError,
  ];

  constructor() {
    this.setupListeners();
  }

  private setupListeners() {
    // Handle START events
    this.router.events
      .pipe(
        filter(event => this.START_EVENTS.some(type => event instanceof type)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setLoading(true);
      });

    // Handle END events
    this.router.events
      .pipe(
        filter(event => this.END_EVENTS.some(type => event instanceof type)),
        takeUntilDestroyed(this.destroyRef),
        debounceTime(50)
      )
      .subscribe(() => {
        this.waitForDocumentReady().then(() => {
          this.setLoading(false);
        });
      });
  }

  private setLoading(loading: boolean) {
    requestAnimationFrame(() => {
      this._isLoading.set(loading);
    });
  }

  private waitForDocumentReady(): Promise<void> {
    return new Promise<void>(resolve => {
      if (document.readyState === 'complete') {
        resolve();
      } else {
        const handler = () => {
          if (document.readyState === 'complete') {
            document.removeEventListener('readystatechange', handler);
            resolve();
          }
        };
        document.addEventListener('readystatechange', handler);
      }
    });
  }
}
